import { apiUrl } from "../../../utils/api";
import type { KontaktRef, RisikoRef, VertragRef } from "./linkSlots";

/**
 * Fetch-/Mapping-Helfer für die K/R/V-Auswahl (Kontakt/Risiko/Vertrag). Aus
 * LinkRow herausgezogen, damit die Zeilen-Darstellung (LinkRow) UND die neue
 * „Tief"-Zuweisung (ZuweisungTief) dieselbe Roh-→Ref-Abbildung nutzen.
 */

export async function fetchJson(url: string): Promise<any[]> {
    try {
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.content)) return data.content;
        return [];
    } catch {
        return [];
    }
}

export const fmtCurrency = (value?: number | null): string | null => {
    if (value == null || !Number.isFinite(Number(value))) return null;
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(value));
};

/** Standard-Adresse einer Kontakt-Zeile: das vom Backend projizierte
 *  `defaultAddress`, sonst die als Standard markierte bzw. erste Adresse. */
const defaultAddressOf = (c: any): KontaktRef["adresse"] => {
    const list: any[] = Array.isArray(c?.addresses) ? c.addresses : [];
    const a = c?.defaultAddress ?? list.find((x) => x?.isDefault) ?? list[0] ?? null;
    if (!a) return null;
    return { street: a.street ?? null, postalCode: a.postalCode ?? null, city: a.city ?? null };
};

export const contactRefFromRow = (c: any): KontaktRef => ({
    id: Number(c.id),
    lastname: c.lastname ?? c.displayName ?? c.companyName ?? null,
    firstName: c.firstName ?? null,
    typ: c.type?.type ?? c.typ ?? null,
    kundeStatus: c.status?.status ?? c.kontaktStatus ?? null,
    adresse: defaultAddressOf(c),
    boType: c.boType ?? null,
    boTeam: c.boTeam ?? null,
    boUser: c.boUser ?? null,
    foUser: c.foUser ?? null,
});

export const risikoRefFromRow = (r: any): RisikoRef => ({
    id: Number(r.id),
    name: r.name ?? r.riskObjectIdentifier ?? null,
    riskType: r.riskType ?? null,
    riskObjectIdentifier: r.riskObjectIdentifier ?? null,
    contractNbr: r.contract?.contractNbr ?? r.contractNbr ?? null,
    contactName: r.contact?.displayName ?? r.contact?.lastname ?? null,
});

export const vertragRefFromRow = (c: any): VertragRef => ({
    id: Number(c.id),
    contractNbr: c.contractNbr ?? null,
    branch: c.branch ?? null,
    premiumAnnualGross: c.premiumAnnualGross ?? null,
    riskObjects: Array.isArray(c.riskObjects) ? c.riskObjects : [],
});

export const riskObjectText = (v: VertragRef): string | null => {
    const parts = (v.riskObjects ?? [])
        .map((r) => r.riskObjectIdentifier || r.name || r.riskType || (r.id ? `Risiko ${r.id}` : ""))
        .filter(Boolean);
    return parts.length ? parts.join(", ") : null;
};

export const selectedMap = <T extends { id: number }>(items: T[]): Map<number, T> =>
    new Map(items.map((it) => [it.id, it]));

/** URL-Bbuilder für die drei Such-/Vorschlags-Quellen (Kontakt/Risiko/Vertrag). */
export const linkQueryUrls = {
    contactsSearch: (q: string) => apiUrl("contact", { search: q, limit: "30", include: "group" }),
    contactsByOwnership: (filters: Record<string, string[]>) =>
        apiUrl("contact", { limit: "5", include: "group", filters: JSON.stringify(filters) }),
    risksSearch: (q: string) => apiUrl("risk-objects", { q, limit: "50" }),
    risksByContact: (contactId: number) => apiUrl(`risk-objects/by-contact/${contactId}`),
    contractsSearch: (q: string) => apiUrl("contracts", { search: q, limit: "50" }),
    contractsByRisk: (riskId: number) => apiUrl(`contracts/by-risk/${riskId}`),
    contractsByContact: (contactId: number) => apiUrl(`contracts/by-contact/${contactId}`),
};
